import type { ComponentProps } from 'react';
import { Heading } from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { CheckCircle, Info, TriangleAlert } from '@repo/media/lucide';

const styles = tv({
  base: [
    'relative w-full p-4 rounded-lg',
    'flex flex-col gap-1.5 text-sm',
    'border border-[var(--color)] bg-primary-contrast',
  ],
  variants: {
    variant: {
      neutral: '[--color:var(--color-secondary-500)]',
      info: '[--color:var(--color-blue-600)]',
      positive: '[--color:var(--color-green-600)]',
      warning: '[--color:var(--color-amber-600)]',
      negative: '[--color:var(--color-red-600)]',
    },
  },
});

export function Callout({
  title,
  children,
  variant = 'neutral',
  className,
  ...props
}: ComponentProps<'div'> & VariantProps<typeof styles> & { title?: string }) {
  const Icon = variant === 'info'
    ? Info
    : variant === 'positive'
      ? CheckCircle
      : variant === 'warning' || variant === 'negative'
        ? TriangleAlert
        : undefined;

  return (
    <div {...props} role="alert" className={twMerge(styles({ variant }), className)}>
      {title && (
        <div className="flex justify-between gap-6 w-full">
          <Heading className="font-medium">{title}</Heading>
          {Icon && <Icon size={20} className="stroke-[var(--color)]" />}
        </div>
      )}
      <div>
        {children}
      </div>
    </div>
  );
}
